/**
 * Learner state.
 *
 * One serialisable object holds everything the platform knows about a student.
 * It is deliberately plain data: the same shape is stored in `localStorage`
 * today and in the `profiles` / `mastery` / `exercise_attempts` tables when the
 * API lands (see `docs/database.md`), and it is what a "download my data" or
 * "delete my data" request operates on (§29).
 */

import type { LevelId } from '@yuan-math/curriculum';
import type { ConceptState } from './mastery.ts';

/** How the student described themselves at sign-up (§27). Never their birthday. */
export type AgeBand = 'kindergarten' | 'elementary' | 'middle' | 'high' | 'university' | 'adult' | 'unknown';

export type AttemptRecord = {
  /** Epoch milliseconds. */
  at: number;
  conceptId: string;
  exerciseId: string;
  correct: boolean;
  status: 'correct' | 'incorrect' | 'malformed';
  difficulty: number;
  responseMs?: number;
  usedHint?: boolean;
  /** The attempt came from the review queue rather than fresh practice. */
  isReview?: boolean;
  misconceptionId?: string;
};

export type Streak = {
  current: number;
  longest: number;
  /** Local calendar day, `YYYY-MM-DD`. Null before the first session. */
  lastActiveDay: string | null;
  totalActiveDays: number;
  /**
   * Unspent rest days. One is earned every `STREAK_FREEZE_EVERY` active days
   * and spent automatically to cover a single missed day.
   *
   * Optional because profiles written before rest days existed must keep
   * loading; absent means none held.
   */
  freezes?: number;
  /** The day a freeze was spent on, so it is only ever spent once. */
  freezeUsedOn?: string | null;
};

export type UnlockedAchievement = { id: string; unlockedAt: number };

/**
 * Today, counted.
 *
 * Daily quests and the answer combo both need to know what has happened since
 * the student's local midnight. Deriving that from the attempt log would work
 * for questions but not for lessons — those carry no timestamp — and the log is
 * bounded at `ATTEMPT_LOG_LIMIT`, so a heavy day could roll its own start out
 * of memory. A handful of counters is cheaper to read and cannot drift out of
 * range.
 *
 * Reset wholesale when the local day changes; see `rollDaily`.
 */
export type DailyProgress = {
  /** Local calendar day, `YYYY-MM-DD`. */
  day: string;
  /** Graded attempts. Malformed input is never counted. */
  attempts: number;
  correct: number;
  /** Correct at difficulty 4–5. */
  hardCorrect: number;
  /** Correct on the first try with no hint shown. */
  unaidedCorrect: number;
  /** Attempts that came from the review queue. */
  reviewAttempts: number;
  lessonsCompleted: number;
  /** Consecutive correct answers right now. A wrong answer resets it to zero. */
  combo: number;
  /** The longest combo reached today. */
  bestCombo: number;
  /** Distinct concepts practised today. Bounded by the day's attempts. */
  conceptIds: string[];
  /**
   * The quests this day is carrying, pinned when the day opens.
   *
   * Pinned rather than recomputed on every read because part of the choice
   * depends on what is due for review, and that changes as the student works —
   * a quest list that re-derived itself could drop a goal the student had
   * already finished.
   */
  questIds: string[];
  /** Quest ids whose reward has already been paid, so it is paid once. */
  claimedQuestIds: string[];
};

export type DiagnosticSummary = {
  completedAt: number;
  /** strand id → estimated proficiency, 0–1. */
  strandScores: Record<string, number>;
  /** Concepts the diagnostic judged already solid. */
  assumedMastered: string[];
  /**
   * The subset of `assumedMastered` the student was actually asked about and
   * got right. The rest were implied by an answer higher in the graph.
   *
   * Kept apart because the two are different kinds of evidence and the profile
   * must not blur them: an answered concept earns a real attempt, an implied
   * one earns an assumption that practice can overwrite. Optional, because
   * profiles written before this existed have no way to tell them apart.
   */
  demonstrated?: string[];
  /**
   * How much of each strand the test was able to reach, 0–1, so a bar reading
   * low can be told apart from a strand nobody asked about.
   */
  strandCoverage?: Record<string, number>;
  recommendedLevel: LevelId;
  startConceptId?: string;
  /**
   * True when nothing was answered wrongly. The test can only probe concepts
   * that have content, so this means "as far as we could measure", not "this
   * is your ceiling" — and the result page says so rather than implying we
   * found a limit.
   */
  answeredEverythingCorrectly?: boolean;
};

/* -------------------------------------------------------------------------- */
/* The RPG slice                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A class is a costume, never a curriculum (docs/rpg.md §10).
 *
 * Nothing anywhere may read this to change difficulty, recommendations, XP or
 * access — every student can learn everything, whatever they chose to wear.
 */
export type RpgClassId = 'wizard' | 'knight' | 'ranger' | 'engineer' | 'scientist';

/**
 * How the character looks, as picked tokens.
 *
 * Closed unions rather than free text, deliberately: an avatar can then be
 * rendered from a fixed set of drawings, serialised safely, and shown to a
 * teacher or parent without moderating what a child typed into it.
 */
export type AvatarConfig = {
  skinTone: 'porcelain' | 'fair' | 'tan' | 'brown' | 'deep';
  hair: 'short' | 'long' | 'curly' | 'braids' | 'buzz' | 'ponytail';
  hairColor: 'black' | 'brown' | 'blonde' | 'red' | 'blue';
  outfit: 'tunic' | 'robe' | 'armour' | 'explorer' | 'lab-coat';
  accessory: 'none' | 'glasses' | 'cape' | 'hat' | 'backpack';
};

/**
 * One graded boss answer, as it is stored. Not a `GradeResult`: the profile
 * keeps what the fight needs to be rebuilt, and a fight only ever needed to
 * know which questions went which way. Malformed input never reaches here at
 * all (`CLAUDE.md` — malformed input is not a wrong answer).
 */
export type BossAnswer = 'correct' | 'wrong';

/**
 * A boss battle in progress, or the last one fought.
 *
 * The smallest thing that can rebuild a fight, and deliberately not the fight
 * itself: `startBattle` in `@yuan-math/world` is a pure function of
 * (learner, boss, attempt), so these two fields plus the learner's id
 * re-derive every question the student saw. Storing the generated exercises
 * instead would put a copy of the exercise bank in every learner's
 * `localStorage` — and a reloaded page would still have to trust it.
 *
 * `answers` is flat, in question order across the whole boss: stage
 * boundaries are a property of the definition, not of the student, so
 * replaying the list against the current definition is what re-derives them.
 */
export type BossBattleRecord = {
  /** Which retry this is, from zero. A retry is a different fight, by seed. */
  attemptIndex: number;
  answers: BossAnswer[];
};

export type RpgCharacter = {
  classId: RpgClassId;
  avatar: AvatarConfig;
  createdAt: number;
  /** The last cosmetic edit. The merge uses it to pick the current look. */
  updatedAt: number;
};

/**
 * Everything the RPG stores about a student.
 *
 * Deliberately small (docs/rpg-implementation-plan.md §0.4): quest and boss
 * *progress* is always derived from definitions and the profile, so what is
 * stored here is only the student's *choices* (character, accepted quests,
 * worn cosmetics) and *settlements* (rewards paid, bosses beaten). Game XP
 * lives in the ordinary `xp` field, not here — the game level and mathematical
 * mastery stay different concepts (docs/rpg.md §46).
 */
/**
 * One thing a student put on the map.
 *
 * `kind` is the vocabulary of `@yuan-math/world`, opaque here for the same
 * reason an item id is: what a bridge *is* belongs with the map, not with the
 * profile that remembers one was placed.
 */
export type Build = {
  kind: string;
  x: number;
  y: number;
  /** When it was placed. The earlier one wins a merge, as an acceptance does. */
  at: number;
};

export type RpgState = {
  /** Absent until the student creates one. */
  character?: RpgCharacter;
  /**
   * Cosmetic currency (docs/rpg.md §31). Earned from quest and boss rewards,
   * never per answer — paying per answer would reward grinding (§47) — and
   * spent only on cosmetics.
   */
  gold: number;
  /** Quest id → when the student accepted it. Progress is derived, not stored. */
  questLog: Record<string, { acceptedAt: number }>;
  /** Quest ids whose reward has been paid, so it is paid once — the same construction as `DailyProgress.claimedQuestIds`. */
  claimedQuestIds: string[];
  clearedBossIds: string[];
  /**
   * Boss id → the fight in progress, or the last one fought.
   *
   * Optional, unlike everything around it: a profile written before boss
   * battles shipped has an `rpg` slice with no `battles` in it, and a required
   * field would be a claim about what is on disk that is not true. Read it
   * through `battlesOf`.
   */
  battles?: Record<string, BossBattleRecord>;
  /**
   * Adventure Journal entries the student has already read.
   *
   * The one stored fact about the journal, and deliberately the only one: every
   * entry is derived from the attempt log and the misconception catalog
   * (`journalEntries` in `@yuan-math/world`), so what is worth remembering is
   * not the entry but whether the student has *seen* it — which is what lets a
   * new one carry a quiet dot instead of the whole page shouting.
   *
   * Ids are opaque here; the journal owns their shape. Optional, like
   * `battles`: a profile written before the journal shipped has an `rpg` slice
   * without it, and a required field would be a claim about what is on disk
   * that is not true.
   */
  journalSeen?: string[];
  /**
   * Misconception ids the student has correctly *named* — the encounter's
   * closing beat (`docs/rpg.md` §18, `sealFor` in `@yuan-math/world`).
   *
   * **This is not a claim about mathematics and nothing may read it as one.**
   * Naming an error is metacognition: it says the student can recognise what
   * they did, not that they have stopped doing it. So it is stored beside the
   * cosmetics rather than beside `concepts`, no recommendation, no mastery and
   * no seal in the world reads it, and the only thing it drives is the
   * bestiary's "named" mark. A student who names every ghost in the game has
   * proved nothing about fractions.
   *
   * A set, so recording one twice changes nothing — the same construction as
   * `claimedQuestIds` and `journalSeen`. Optional, like both of them: a profile
   * written before the encounter shipped has an `rpg` slice without it, and a
   * required field would be a claim about what is on disk that is not true.
   */
  namedTrickIds?: string[];
  /**
   * What the student has built on the overworld — bridges, islands and houses
   * (`build.ts` in `@yuan-math/world`).
   *
   * Stored, unlike where a student is *standing*, and the difference is worth
   * stating: a position is a fact about a moment and a building is a thing
   * somebody made. It belongs in the same category as `inventory` — a choice
   * the student made about how their world looks, kept because they made it.
   *
   * **Nothing here is a fact about mathematics and nothing may read it as one.**
   * No recommendation, no mastery, no seal and no readiness check reads a
   * build; the whole of what one does is add ground and a drawing. A student
   * who has built a hundred houses has proved nothing about fractions.
   *
   * Optional, like `battles` and `namedTrickIds`: a profile written before
   * building shipped has an `rpg` slice without it, and a required field would
   * be a claim about what is on disk that is not true.
   */
  builds?: Build[];
  /** Cosmetic item ids owned. Empty until PR 12 sells anything. */
  inventory: string[];
  /** Slot → item id worn there. */
  equipped: Record<string, string>;
};

export type LearnerProfile = {
  id: string;
  displayName: string;
  ageBand: AgeBand;
  /** The level the student is working at. Editable; never locks content. */
  levelId?: LevelId;
  createdAt: number;

  xp: number;
  streak: Streak;

  /** Per-concept mastery and review state, keyed by concept id. */
  concepts: Record<string, ConceptState>;
  completedLessonIds: string[];
  achievements: UnlockedAchievement[];

  /** Recent attempts, newest last. Bounded — older attempts are aggregated. */
  attempts: AttemptRecord[];

  stats: {
    totalAttempts: number;
    totalCorrect: number;
    totalStudyMs: number;
    lessonsCompleted: number;
    /** Daily quests completed, all time. Optional for profiles predating them. */
    questsCompleted?: number;
    /** The longest run of consecutive correct answers, all time. */
    bestCombo?: number;
  };

  /** What has happened since the student's local midnight. */
  daily?: DailyProgress;

  /**
   * How many days a week the student is aiming to practise. Their own choice,
   * not a target the platform sets — see `weeklyGoal`.
   */
  weeklyGoalDays?: number;

  diagnostic?: DiagnosticSummary;
  /** A concept the student is aiming for, used by the Math Map path finder. */
  goalConceptId?: string;

  /**
   * The adventure. Optional because profiles written before the RPG existed
   * must keep loading — the same back-compat pattern as `Streak.freezes`;
   * absent means the student has not entered the adventure yet.
   */
  rpg?: RpgState;
};

export const ATTEMPT_LOG_LIMIT = 400;

export function createProfile(input: {
  id: string;
  displayName: string;
  ageBand?: AgeBand;
  levelId?: LevelId;
  now: number;
}): LearnerProfile {
  return {
    id: input.id,
    displayName: input.displayName,
    ageBand: input.ageBand ?? 'unknown',
    ...(input.levelId ? { levelId: input.levelId } : {}),
    createdAt: input.now,
    xp: 0,
    streak: { current: 0, longest: 0, lastActiveDay: null, totalActiveDays: 0, freezes: 0, freezeUsedOn: null },
    concepts: {},
    completedLessonIds: [],
    achievements: [],
    attempts: [],
    stats: { totalAttempts: 0, totalCorrect: 0, totalStudyMs: 0, lessonsCompleted: 0, questsCompleted: 0, bestCombo: 0 },
  };
}
